import { capitalizeFirstLetter, type IconName } from '@sg/garnish';
import { type Order } from '@sg/graphql-schema';
import { PaymentProcessor } from '@sg/shared-be/features/payment';

export const parsePayments = (payments?: Order['payments']) => {
  return (
    payments?.map((payment) => ({
      amount: parseAmount(payment.amountInCents),
      type: parseProcessor(payment.processor),
      metadata: parseMetadata(payment),
      icon: parseIcon(payment),
    })) ?? []
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseAmount = (amount: number) => {
  return amount / 100;
};

const parseProcessor = (
  processor: string,
): 'GIFT_CARD' | 'STIPEND' | 'CREDIT_CARD' => {
  if (processor === PaymentProcessor.PaytronixGiftCard) {
    return 'GIFT_CARD';
  }

  if (processor === PaymentProcessor.Stipend) {
    return 'STIPEND';
  }

  return 'CREDIT_CARD';
};

const parseMetadata = (payment: Payment) => {
  const { type, last4 } = payment;

  if (!type) return;

  const formattedType = capitalizeFirstLetter(type.toLowerCase());

  if (payment.last4) {
    return `${formattedType} ${last4}`;
  }

  return formattedType;
};

const parseIcon = (payment: Payment): IconName | undefined => {
  const { type } = payment;

  if (!type) return;

  if (type.toLowerCase().includes('visa')) {
    return 'IconCcVisa';
  }

  if (type.toLowerCase().includes('mastercard')) {
    return 'IconCcMasterCard';
  }

  if (type.toLowerCase().includes('american express')) {
    return 'IconCcAmex';
  }

  if (type.toLowerCase().includes('amex')) {
    return 'IconCcAmex';
  }

  if (type.toLowerCase().includes('discover')) {
    return 'IconCcDiscover';
  }

  return 'IconCreditCard';
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Payment = NonNullable<Order['payments']>[number];
